import { MenuItem, TextField } from '@mui/material';

import { TagsInput } from '../shared/TagsInput';
import {
    isPortalPostState,
    PORTAL_POST_ID_MAX_LENGTH,
    PortalPostState,
    UserRoles,
} from '@lifeserver/shared';

import { useIsAuthorized } from '../hooks/useIsAuthorized';

import styles from './PostMetaFields.module.scss';

import { APP_VER_REGEX } from './templates/validators';

export function PostMetaFields({
    id,
    draftId,
    setDraftId,
    tagOptions,
    selectedTags,
    setSelectedTags,
    isLoading,
    postState,
    setPostState,
    initialPostState,
    minimumVersion,
    onMinimumVersionChange,
    notes,
    setNotes,
}: {
    id?: string;
    draftId: string;
    setDraftId: (v: string) => void;
    tagOptions: Array<{ name: string }>;
    selectedTags: Array<{ name: string }>;
    setSelectedTags: (tags: Array<{ name: string }>) => void;
    isLoading: boolean;
    postState: PortalPostState;
    setPostState: (state: PortalPostState) => void;
    initialPostState?: PortalPostState;
    minimumVersion: string | null;
    onMinimumVersionChange?: (minimumVersion: string | null) => void;
    notes: string;
    setNotes: (notes: string) => void;
}) {
    const canSetReady = useIsAuthorized(UserRoles.PortalWriteSchedule);

    const isIdTooLong = draftId.length > PORTAL_POST_ID_MAX_LENGTH;

    const minimumVersionInvalid = minimumVersion
        ? !APP_VER_REGEX.test(minimumVersion)
        : false;

    return (
        <div className={styles.container}>
            <div className={styles.topRow}>
                <TextField
                    label="Name"
                    disabled={Boolean(id)}
                    value={id ?? draftId}
                    onChange={(e) => setDraftId(e.currentTarget.value)}
                    className={styles.nameField}
                    required
                    error={isIdTooLong}
                    helperText={
                        isIdTooLong
                            ? `Name must not exceed ${PORTAL_POST_ID_MAX_LENGTH} characters (currently ${draftId.length})`
                            : undefined
                    }
                />
                <TagsInput
                    value={selectedTags}
                    onChange={setSelectedTags}
                    options={tagOptions}
                    loading={isLoading}
                    className={styles.tagsField}
                />

                <TextField
                    label="Minimum version"
                    value={minimumVersion ?? ''}
                    error={minimumVersionInvalid}
                    helperText={
                        minimumVersionInvalid
                            ? 'Needs to be a valid application version'
                            : 'Note: Is currently only in-use in event calendar posts.'
                    }
                    onChange={(e) => {
                        onMinimumVersionChange?.(e.target.value || null);
                    }}
                />
                <TextField
                    id="post-state"
                    select
                    label="State"
                    value={postState}
                    disabled={initialPostState === 'ready'}
                    onChange={(e) =>
                        isPortalPostState(e.target.value) &&
                        setPostState(e.target.value)
                    }
                    className={styles.stateInput}
                >
                    <MenuItem value="todo">To Do</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="in_review">In Review</MenuItem>
                    <MenuItem
                        value="ready"
                        disabled={
                            canSetReady.isLoading || !canSetReady.isAuthorized
                        }
                    >
                        Ready
                    </MenuItem>
                </TextField>
            </div>

            {postState === 'ready' && (
                <p className={styles.disclaimer}>
                    Please note that a post that is "ready" cannot change the
                    status once it is saved.
                </p>
            )}

            <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                className={styles.notesField}
                multiline
                rows={1}
            />
        </div>
    );
}
